import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { StatWidget } from '@/components/widgets/StatWidget';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Select2 } from '@/components/forms/Select2';
import type { Select2Option } from '@/components/forms/Select2';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { ProviderTransactionRowDto, ProviderTransactionsInput, ResultDto } from '@/api/types/providerFunds';
import { useEnum } from '@/features/common/enums/useEnumOptions';
import type { ProviderFundsConfig } from './config';
import { daysAgoIso, yesterdayIso } from './config';
import { renderDateTime, renderNumber, renderNumberOrDash, renderTextOrDash } from './renderers';
import { ManualTransactionCard } from './ManualTransactionCard';

interface TransactionsTabProps {
  config: ProviderFundsConfig;
  canViewRealCashBalance: boolean;
  canAddTransaction: boolean;
  selectedProviderId: string | null;
  selectedProviderName: string | null;
}

export function TransactionsTab({
  config,
  canViewRealCashBalance,
  canAddTransaction,
  selectedProviderId,
  selectedProviderName,
}: TransactionsTabProps) {
  const toast = useToast();
  const yesterday = yesterdayIso();

  const [providerId, setProviderId] = useState<string | null>(selectedProviderId);
  const [providerName, setProviderName] = useState<string>(selectedProviderName ?? '');
  const [range, setRange] = useState<{ start: string; end: string }>({ start: daysAgoIso(30), end: daysAgoIso(1) });
  const [type, setType] = useState<string | null>(null);
  const [showCard, setShowCard] = useState(false);

  // Applied values drive the grid + widgets (Fetch / provider change).
  const [applied, setApplied] = useState<{ providerId: string; startDate: string; endDate: string; type: string | null } | null>(
    selectedProviderId ? { providerId: selectedProviderId, startDate: daysAgoIso(30), endDate: daysAgoIso(1), type: null } : null,
  );

  // Cross-tab preselect: when the parent sets a provider, load it.
  useEffect(() => {
    if (selectedProviderId) {
      setProviderId(selectedProviderId);
      setProviderName(selectedProviderName ?? '');
      setRange({ start: daysAgoIso(30), end: daysAgoIso(1) });
      setApplied({ providerId: selectedProviderId, startDate: daysAgoIso(30), endDate: daysAgoIso(1), type: null });
      setShowCard(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProviderId, selectedProviderName]);

  const providersQuery = useQuery({
    queryKey: [config.key, 'providers'],
    queryFn: config.api.getProviders,
    staleTime: Infinity,
  });
  const providerOptions: Select2Option[] = useMemo(
    () => (providersQuery.data ?? []).map((p: ResultDto) => ({ value: p.value, label: p.text })),
    [providersQuery.data],
  );
  // Display names (duplicate labels kept); the server parses the display-name
  // string the dropdown carries. The #value suffix keeps duplicate labels unique.
  const transactionTypes = useEnum('ProviderTransactionType');
  const typeOptions: Select2Option[] = useMemo(
    () => transactionTypes.map((o) => ({ value: `${o.label}#${o.value}`, label: o.label })),
    [transactionTypes],
  );
  // The dropdown value carries a unique suffix (duplicate labels must stay); strip it
  // before sending the display-name string the server parses.
  const [typeValue, setTypeValue] = useState<string | null>(null);

  // Provider details (for the manual-tx card currency / opening date / lock info).
  const detailsQuery = useQuery({
    queryKey: [config.key, 'tx-details', providerId],
    queryFn: () => config.api.getProviderDetails({ providerId: providerId! }),
    enabled: Boolean(providerId) && (showCard || config.lockAwareManualCard),
  });

  const boxesQuery = useQuery({
    queryKey: [config.key, 'boxes', applied],
    queryFn: () => config.api.getBoxes({ providerId: applied!.providerId, startDate: applied!.startDate, endDate: applied!.endDate, type: applied!.type }),
    enabled: Boolean(applied),
  });

  const table = useServerDataTable<ProviderTransactionRowDto, Record<string, unknown>>({
    queryKey: [config.key, 'tx-rows'],
    fetcher: (req) =>
      config.api.getTransactionsForDataTable({
        providerId: applied!.providerId,
        startDate: applied!.startDate,
        endDate: applied!.endDate,
        type: applied!.type,
        skipCount: req.skipCount,
        maxResultCount: req.maxResultCount,
        sorting: req.sorting,
      } as ProviderTransactionsInput),
    pageSize: 5,
    enabled: Boolean(applied),
  });

  function changeProvider(v: string | null) {
    setProviderId(v);
    const match = providerOptions.find((o) => String(o.value) === v);
    setProviderName(match ? match.label : '');
    setShowCard(false);
  }

  function fetchData() {
    if (!providerId) {
      toast.warning('Please select a provider.');
      return;
    }
    setApplied({ providerId, startDate: range.start, endDate: range.end, type: stripType(typeValue) });
    table.setPageIndex(0);
  }

  function stripType(v: string | null): string | null {
    if (!v) return null;
    return v.includes('#') ? v.slice(0, v.lastIndexOf('#')) : v;
  }

  async function extract() {
    if (!applied) {
      toast.warning('Please fetch transactions first.');
      return;
    }
    try {
      await config.api.downloadTransactionsExcel(
        { providerId: applied.providerId, startDate: applied.startDate, endDate: applied.endDate, type: applied.type },
        config.transactionsExcelName,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to export transactions.');
    }
  }

  const b = boxesQuery.data;
  const reserves = config.absReserves ? Math.abs(b?.reservesCaptured ?? 0) : b?.reservesCaptured ?? 0;

  const columns = useMemo<DataGridColumn<ProviderTransactionRowDto>[]>(() => {
    const cols: DataGridColumn<ProviderTransactionRowDto>[] = [
      { key: 'dateOfTransaction', title: 'Date Time', sortable: true, value: (r) => r.dateOfTransaction, cell: (r) => renderDateTime(r.dateOfTransaction) },
      { key: 'in', title: 'IN', sortable: false, className: 'text-end', value: (r) => r.in, cell: (r) => renderNumberOrDash(r.in) },
      { key: 'out', title: 'OUT', sortable: false, className: 'text-end', value: (r) => r.out, cell: (r) => renderNumberOrDash(r.out) },
      { key: 'type', title: 'Type', sortable: false, value: (r) => r.type },
      { key: 'inRc', title: 'IN RC', sortable: false, className: 'text-end', value: (r) => r.inRc, cell: (r) => renderNumberOrDash(r.inRc) },
      { key: 'outRc', title: 'OUT RC', sortable: false, className: 'text-end', value: (r) => r.outRc, cell: (r) => renderNumberOrDash(r.outRc) },
      { key: 'cashBookBalance', title: 'Cash Book Balance', sortable: false, className: 'text-end', value: (r) => r.cashBookBalance, cell: (r) => renderNumber(r.cashBookBalance) },
    ];
    if (canViewRealCashBalance) {
      cols.push({ key: 'cumBalance', title: 'Real Cash Balance', sortable: false, className: 'text-end', value: (r) => r.cumBalance, cell: (r) => renderNumber(r.cumBalance) });
    }
    cols.push(
      { key: 'availableBalance', title: 'Available Balance', sortable: false, className: 'text-end', value: (r) => r.availableBalance, cell: (r) => renderNumber(r.availableBalance) },
      { key: 'withdrawableBalance', title: 'Withdrawable Balance', sortable: false, className: 'text-end', value: (r) => r.withdrawableBalance, cell: (r) => renderNumber(r.withdrawableBalance) },
      { key: 'cumulativeReservesAccount', title: 'Reserves', sortable: false, className: 'text-end', value: (r) => r.cumulativeReservesAccount, cell: (r) => renderNumber(r.cumulativeReservesAccount) },
      { key: 'cashBookBalanceRc', title: 'Cash Book Balance RC', sortable: false, className: 'text-end', value: (r) => r.cashBookBalanceRc, cell: (r) => renderNumber(r.cashBookBalanceRc) },
    );
    if (canViewRealCashBalance) {
      cols.push({ key: 'cumBalanceRc', title: 'Real Cash Balance RC', sortable: false, className: 'text-end', value: (r) => r.cumBalanceRc, cell: (r) => renderNumber(r.cumBalanceRc) });
    }
    cols.push(
      { key: 'availableBalanceRc', title: 'Available Balance RC', sortable: false, className: 'text-end', value: (r) => r.availableBalanceRc, cell: (r) => renderNumber(r.availableBalanceRc) },
      { key: 'withdrawableBalanceRc', title: 'Withdrawable Balance RC', sortable: false, className: 'text-end', value: (r) => r.withdrawableBalanceRc, cell: (r) => renderNumber(r.withdrawableBalanceRc) },
      { key: 'cumulativeReservesAccountRc', title: 'Reserves RC', sortable: false, className: 'text-end', value: (r) => r.cumulativeReservesAccountRc, cell: (r) => renderNumber(r.cumulativeReservesAccountRc) },
      { key: 'comment', title: 'Comment', sortable: false, value: (r) => r.comment },
      { key: 'relatedTransaction', title: 'Related Transaction', sortable: false, value: (r) => r.relatedTransaction, cell: (r) => renderTextOrDash(r.relatedTransaction) },
      { key: 'login', title: 'Login', sortable: false, value: (r) => r.login, cell: (r) => renderTextOrDash(r.login) },
      { key: 'connector', title: 'Connector', sortable: false, value: (r) => r.connector, cell: (r) => renderTextOrDash(r.connector) },
    );
    return cols;
  }, [canViewRealCashBalance]);

  const fields = [
    <Select2 key="provider" label="Provider" placeholder="Select Provider" value={providerId} options={providerOptions} onChange={changeProvider} />,
    {
      node: <DateRangePicker key="range" label="Date" mode="range" value={range} maxDate={yesterday} onChange={setRange} />,
      pinned: true,
      width: 270,
    },
    <Select2 key="type" label="Type" placeholder="All" value={typeValue} options={typeOptions} onChange={setTypeValue} />,
  ];
  const actions = (
    <>
      <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData} loading={table.isFetching || boxesQuery.isFetching}>
        Fetch
      </AsyncButton>
      {config.hasTransactionsExcel && (
        <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>
          Extract
        </AsyncButton>
      )}
      {canAddTransaction && (
        <AsyncButton
          className="btn btn-outline-dark btn-sm"
          icon="fa fa-plus"
          onClick={() => {
            if (!providerId) {
              toast.warning('Please select a provider.');
              return;
            }
            setShowCard((v) => !v);
          }}
        >
          Add Transaction
        </AsyncButton>
      )}
    </>
  );

  // avoid unused-var lint for setType/type (kept for symmetry); typeValue is the source.
  void type;
  void setType;

  return (
    <div>
      <div className="mb-3">
        <FilterBar card={false} fields={fields} actions={actions} inlineActions />
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4 col-lg">
          <StatWidget label="Count Of Deposits" value={(b?.depositCount ?? 0).toLocaleString()} icon="fe fe-arrow-down-circle" theme="success" />
        </div>
        <div className="col-md-4 col-lg">
          <StatWidget label="Count Of Withdrawals" value={(b?.withdrawalsCount ?? 0).toLocaleString()} icon="fe fe-arrow-up-circle" theme="danger" />
        </div>
        {config.showFeeWidgets && (
          <>
            <div className="col-md-4 col-lg">
              <StatWidget label="Deposit Fees" value={renderNumber(b?.depositFee ?? 0)} icon="fe fe-percent" theme="info" />
            </div>
            <div className="col-md-4 col-lg">
              <StatWidget label="Withdrawal Fees" value={renderNumber(b?.withdrawalsFee ?? 0)} icon="fe fe-percent" theme="warning" />
            </div>
          </>
        )}
        <div className="col-md-4 col-lg">
          <StatWidget label="Reserves Captured" value={renderNumber(reserves)} icon="fe fe-shield" theme="primary" />
        </div>
        <div className="col-md-4 col-lg">
          {config.excessTooltip ? (
            <span title="Deposits - Withdrawals - Reserves - All fees" className="d-block h-100">
              <StatWidget label="Excess" value={renderNumber(b?.excess ?? 0)} icon="fe fe-trending-up" theme="neutral" />
            </span>
          ) : (
            <StatWidget label="Excess" value={renderNumber(b?.excess ?? 0)} icon="fe fe-trending-up" theme="neutral" />
          )}
        </div>
      </div>

      {showCard && canAddTransaction && providerId && (
        <ManualTransactionCard
          config={config}
          providerId={providerId}
          providerName={providerName}
          details={detailsQuery.data}
          canViewRealCashBalance={canViewRealCashBalance}
          onCreated={() => {
            setShowCard(false);
            table.refetch();
            boxesQuery.refetch();
          }}
          onCancel={() => setShowCard(false)}
        />
      )}

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => `${r.dateOfTransaction}-${r.type}-${r.in}-${r.out}-${r.comment}`}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
        emptyMessage={!applied ? 'Select a provider and fetch.' : table.isLoading ? 'Loading…' : 'No data available'}
      />
    </div>
  );
}
